import React, { useEffect, useState } from "react";
import api, { apiError, openPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField, AreaField } from "@/components/fields";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserList, Plus, PencilSimple, FileText, HandCoins, Money } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", id_number: "", email: "", phone: "", postal_address: "", preferred_communication: "email" };

export default function Landlords() {
  const { user, company } = useAuth();
  const cur = company?.currency || "KES";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [advance, setAdvance] = useState(null);
  const [payout, setPayout] = useState(null);
  const [amt, setAmt] = useState(0);
  const [note, setNote] = useState("");
  const [cheque, setCheque] = useState("");

  const canEdit = ["property_manager", "property_admin"].includes(user.role);
  const canPayout = ["accountant", "property_manager"].includes(user.role);

  const load = () => api.get("/landlords").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) return toast.error("Landlord name is required");
    try {
      if (editId) await api.put(`/landlords/${editId}`, form);
      else await api.post("/landlords", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const viewStmt = async (l) => {
    try { const r = await api.get(`/landlords/${l.id}/statement`); setStmt({ landlord: l, ...r.data }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitAdvance = async () => {
    try { await api.post("/landlords/advance", { landlord_id: advance.id, amount: Number(amt), note });
      toast.success("Advance recorded"); setAdvance(null); setAmt(0); setNote(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitPayout = async () => {
    try { const r = await api.post(`/landlords/${payout.id}/payout`, { landlord_id: payout.id, amount: Number(amt), cheque_number: cheque, note });
      toast.success(`Voucher ${r.data.voucher_number} created`); setPayout(null); setAmt(0); setNote(""); setCheque("");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title="Landlords" subtitle="Property owners, statements & payouts" icon={UserList}
        actions={canEdit && <Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-landlord-btn" className="gap-2"><Plus size={16} /> Add Landlord</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={UserList} title="No landlords yet" message="Register a landlord to link their properties."
          action={canEdit && <Button onClick={() => setOpen(true)}><Plus size={16} className="mr-2" />Add Landlord</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Preferred</TableHead>
                <TableHead className="text-right">Advance Bal.</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} data-testid={`landlord-row-${l.id}`}>
                    <TableCell><div className="font-medium">{l.name}</div><div className="text-xs text-muted-foreground">{l.id_number}</div></TableCell>
                    <TableCell className="text-sm">{l.phone}<div className="text-xs text-muted-foreground">{l.email}</div></TableCell>
                    <TableCell className="capitalize text-sm">{l.preferred_communication}</TableCell>
                    <TableCell className="text-right font-mono">{money(l.advance_balance || 0, cur)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end flex-wrap">
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => viewStmt(l)} data-testid={`stmt-${l.id}`}><FileText size={15} /> Statement</Button>
                        {canPayout && <Button size="icon" variant="ghost" title="Advance" onClick={() => setAdvance(l)}><HandCoins size={16} /></Button>}
                        {canPayout && <Button size="icon" variant="ghost" title="Payout" onClick={() => setPayout(l)} data-testid={`payout-${l.id}`}><Money size={16} /></Button>}
                        {canEdit && <Button size="icon" variant="ghost" onClick={() => { setForm({ ...empty, ...l }); setEditId(l.id); setOpen(true); }}><PencilSimple size={16} /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit Landlord" : "Add Landlord"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Name" value={form.name} onChange={(v) => set("name", v)} required testId="landlord-name" />
            <TextField label="ID / Reg No." value={form.id_number} onChange={(v) => set("id_number", v)} />
            <TextField label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} />
            <TextField label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <SelectField label="Preferred Comm." value={form.preferred_communication} onChange={(v) => set("preferred_communication", v)} options={["email", "sms", "whatsapp", "phone"]} />
          </div>
          <div className="mt-4"><AreaField label="Postal Address" value={form.postal_address} onChange={(v) => set("postal_address", v)} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-landlord-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Statement */}
      <Dialog open={!!stmt} onOpenChange={() => setStmt(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Statement — {stmt?.landlord?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5 text-sm">
            {[["Gross Rental Income", stmt?.gross], ["Less Management Fee", stmt?.management_total],
              ["Less VAT on Mgmt Fee", stmt?.mgmt_vat], ["Less Expenses & Deductions", stmt?.expenses_total]].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1.5 border-b border-border"><span>{k}</span><span className="font-mono">{money(v, cur)}</span></div>
            ))}
            <div className="flex justify-between py-2 font-semibold text-primary"><span>Net Amount Payable</span><span className="font-mono">{money(stmt?.net_payable, cur)}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => openPdf(`/landlords/${stmt.landlord.id}/statement/pdf`)} className="gap-2"><FileText size={16} /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance */}
      <Dialog open={!!advance} onOpenChange={() => setAdvance(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Landlord Advance — {advance?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <TextField label="Advance Amount" type="number" value={amt} onChange={setAmt} testId="advance-amount" />
            <AreaField label="Note" value={note} onChange={setNote} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAdvance(null)}>Cancel</Button><Button onClick={submitAdvance}>Record Advance</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout */}
      <Dialog open={!!payout} onOpenChange={() => setPayout(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Pay Landlord — {payout?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <TextField label="Net Amount" type="number" value={amt} onChange={setAmt} testId="payout-amount" />
            <TextField label="Cheque Number" value={cheque} onChange={setCheque} testId="payout-cheque" />
            <AreaField label="Being Payment For" value={note} onChange={setNote} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayout(null)}>Cancel</Button><Button onClick={submitPayout} data-testid="confirm-payout-btn">Generate Voucher</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
